import React from 'react';

import { reloadCompany } from 'common/actions/company';
import { type PostType } from 'common/actions/queueItemQueries';
import Accordion from 'common/common/Accordion';
import {
  getEncodedQueryParams,
  getQueryParams,
} from 'common/containers/QueuePostItemListContainer';
import connect from 'common/core/connect';
import CheckboxWithLabel from 'common/ui/CheckboxWithLabel';

import { PostTypes } from './consts';
import FiltersPopover from './FiltersPopover';
import useFilterControls from './useFilterControls';

import type { Board } from 'common/api/endpoints/boards';
import type { Dispatch, State } from 'redux-connect';

import 'css/components/subdomain/admin/_AdminQueueFilters.scss';

type OwnProps = {
  boards: Board[];
};

type ConnectProps = {
  reloadCompany: () => void;
  queryState: Record<string, string | string[] | undefined> | null;
};

type Props = OwnProps & ConnectProps;

const PostsFilters = ({ boards, queryState, reloadCompany }: Props) => {
  const { queryParams, updateFilters } = useFilterControls({
    getQueryParams,
    getEncodedQueryParams,
    preferencesKey: 'queuePostFilters',
    queryState,
    reloadCompany,
  });

  const resetFilters = () =>
    updateFilters({ ...queryParams, postTypes: undefined, boardURLNames: undefined });

  const enabledBoards = queryParams.boardURLNames ?? [];
  const enabledPostTypes = queryParams.postTypes ?? [];

  const onToggleBoard = (board: Board) => {
    const updatedFilteredBoards = enabledBoards.includes(board.urlName)
      ? enabledBoards.filter((b: string) => b !== board.urlName)
      : enabledBoards.concat(board.urlName);

    updateFilters({
      ...queryParams,
      boardURLNames: updatedFilteredBoards.length ? updatedFilteredBoards : undefined,
    });
  };

  const onTogglePostType = (postType: PostType) => {
    const updatedPostTypes = enabledPostTypes.includes(postType)
      ? enabledPostTypes.filter((pt: string) => pt !== postType)
      : enabledPostTypes.concat(postType);

    updateFilters({
      ...queryParams,
      postTypes: updatedPostTypes.length ? updatedPostTypes : undefined,
    });
  };

  if (!boards.length) {
    return null;
  }

  return (
    <FiltersPopover resetFilters={resetFilters}>
      <Accordion className="filterAccordion" title="Boards">
        <div className="accordionCheckboxes">
          {boards.map((board) => (
            <CheckboxWithLabel
              checked={enabledBoards.includes(board.urlName)}
              onChange={() => onToggleBoard(board)}
              key={board._id}
              size="medium">
              {board.name}
            </CheckboxWithLabel>
          ))}
        </div>
      </Accordion>
      <Accordion className="filterAccordion" title="Types">
        <div className="accordionCheckboxes">
          {PostTypes.map((type) => (
            <CheckboxWithLabel
              checked={enabledPostTypes.includes(type.name)}
              onChange={() => onTogglePostType(type.name)}
              key={type.name}
              size="medium">
              {type.label}
            </CheckboxWithLabel>
          ))}
        </div>
      </Accordion>
    </FiltersPopover>
  );
};

// TODO: remove cast after `connect` is typed
export default connect(
  (state: State) => ({
    queryState: state.routing.locationBeforeTransitions.query,
  }),
  (dispatch: Dispatch) => ({
    reloadCompany: () => dispatch(reloadCompany()),
  })
)(PostsFilters) as unknown as React.FC<OwnProps>;
